import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, X, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * Smart, contextual upgrade prompt. Renders ONLY for genuinely free users —
 * `user.plan` is the *effective* plan, so paid users AND everyone under global
 * Free Mode get plan "pro"/"plus" and therefore never see any nudge.
 *
 * variant="card"   → dismissible value-prop card (dashboard / list pages)
 * variant="inline" → slim contextual banner (e.g. a locked feature tab)
 */
export default function UpgradeNudge({
  variant = "card",
  feature,
  title = "Unlock everything",
  message,
  dismissKey,
  className = "",
}) {
  const { user } = useAuth();
  const dismissedInitially = dismissKey
    ? sessionStorage.getItem(`upgrade-nudge-dismissed:${dismissKey}`) === "1"
    : false;
  const [dismissed, setDismissed] = useState(dismissedInitially);

  // Effective plan: paid OR global free-mode => not "free" => no nudge.
  if (!user || user.plan !== "free" || dismissed) return null;

  const dismiss = () => {
    if (dismissKey) sessionStorage.setItem(`upgrade-nudge-dismissed:${dismissKey}`, "1");
    setDismissed(true);
  };

  if (variant === "inline") {
    return (
      <div
        data-testid="upgrade-nudge-inline"
        className={`flex items-center gap-3 rounded-xl border border-[#7C5CFF]/30 bg-gradient-to-r from-[#7C5CFF]/12 to-[#5AC8FA]/8 px-4 py-3 ${className}`}
      >
        <Lock size={15} className="text-[#B8A4FF] shrink-0" />
        <p className="text-[13px] text-[#D8D5E0] flex-1 leading-snug">
          {message || (
            <>
              <span className="font-semibold text-white">{feature || "This"}</span> is a Plus
              feature. Upgrade to unlock it.
            </>
          )}
        </p>
        <Link
          to="/app/billing"
          data-testid="upgrade-nudge-inline-cta"
          className="shrink-0 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#7C5CFF] to-[#5AC8FA] px-3.5 py-1.5 text-[12px] font-bold text-white hover:opacity-90 transition-opacity"
        >
          Upgrade <ArrowRight size={12} />
        </Link>
      </div>
    );
  }

  return (
    <div
      data-testid="upgrade-nudge-card"
      className={`relative overflow-hidden rounded-2xl border border-[#7C5CFF]/30 bg-gradient-to-br from-[#7C5CFF]/14 via-[#14121d] to-[#5AC8FA]/8 p-5 ${className}`}
    >
      {dismissKey && (
        <button
          onClick={dismiss}
          data-testid="upgrade-nudge-dismiss"
          aria-label="Dismiss"
          className="absolute top-3 right-3 text-[#8E8B99] hover:text-white transition-colors"
        >
          <X size={16} />
        </button>
      )}
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-[#7C5CFF]/25">
          <Sparkles size={15} className="text-[#B8A4FF]" />
        </span>
        <h3 className="text-[15px] font-semibold text-white">{title}</h3>
      </div>
      <p className="text-[13px] text-[#B0AEBC] leading-relaxed max-w-md">
        {message ||
          "Go unlimited — audio, video & YouTube imports, timed mock exams with OCR grading, the AI tutor, and an ad-free experience."}
      </p>
      <Link
        to="/app/billing"
        data-testid="upgrade-nudge-card-cta"
        className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#7C5CFF] to-[#5AC8FA] px-4 py-2 text-[13px] font-bold text-white hover:opacity-90 transition-opacity"
        style={{ boxShadow: "0 0 18px -4px rgba(124,92,255,0.6)" }}
      >
        See plans <ArrowRight size={14} />
      </Link>
    </div>
  );
}
